import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2, Loader2, X, Save, GripVertical } from "lucide-react";
import { api, apiErr, resolveUrl } from "@/lib/api";
import { ImageUpload, ImageListUpload } from "./ImageUpload";

const ICON_OPTIONS = ["Trophy", "BookOpenCheck", "Building2", "GraduationCap", "Users", "Target", "Rocket", "Award", "Brain", "Cpu", "Lightbulb", "Medal"];

const StringList = ({ values = [], onChange }) => (
  <div className="space-y-2">
    {(values || []).map((v, i) => (
      <div key={i} className="flex gap-2">
        <input value={v} onChange={(e) => onChange(values.map((x, k) => (k === i ? e.target.value : x)))} className="flex-1 rounded-lg bg-navy-soft border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan/50" />
        <button type="button" onClick={() => onChange(values.filter((_, k) => k !== i))} className="grid place-items-center w-9 rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/40">
          <X className="w-4 h-4" />
        </button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...(values || []), ""])} className="inline-flex items-center gap-1.5 text-sm text-cyan hover:gap-2.5 transition-all">
      <Plus className="w-4 h-4" /> Tambah poin
    </button>
  </div>
);

const Field = ({ f, value, onChange }) => {
  const base = "w-full rounded-lg bg-navy-soft border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan/50";
  if (f.type === "image") return <ImageUpload label={f.label} value={value} onChange={onChange} testid={`upload-${f.key}`} />;
  if (f.type === "imageList") return <ImageListUpload values={value || []} onChange={onChange} testid={`upload-${f.key}`} />;
  if (f.type === "stringList") return (<div><label className="text-sm text-slate-300 font-medium">{f.label}</label><div className="mt-2"><StringList values={value || []} onChange={onChange} /></div></div>);
  if (f.type === "iconSelect")
    return (
      <div>
        <label className="text-sm text-slate-300 font-medium">{f.label}</label>
        <div className="mt-2 flex items-center gap-3">
          {(() => { const I = Icons[value] || Icons.Sparkles; return <span className="grid place-items-center w-10 h-10 rounded-lg bg-cyan/10 border border-white/10 text-cyan"><I className="w-5 h-5" /></span>; })()}
          <select value={value || "Trophy"} onChange={(e) => onChange(e.target.value)} className={base}>
            {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>
    );
  if (f.type === "textarea") return (<div><label className="text-sm text-slate-300 font-medium">{f.label}</label><textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`mt-2 resize-none ${base}`} data-testid={`field-${f.key}`} /></div>);
  return (<div><label className="text-sm text-slate-300 font-medium">{f.label}</label><input type={f.type === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`mt-2 ${base}`} data-testid={`field-${f.key}`} /></div>);
};

export const EntityPanel = ({ title, collection, fields, makeEmpty, summary }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/content/${collection}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection]);

  const persistOrder = async () => {
    try {
      await api.post(`/admin/content/${collection}/reorder`, { ids: itemsRef.current.map((i) => i.id) });
      toast.success("Urutan disimpan");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...editing };
    fields.forEach((f) => { if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0; });
    try {
      if (payload.id) await api.put(`/admin/content/${collection}/${payload.id}`, payload);
      else await api.post(`/admin/content/${collection}`, payload);
      toast.success("Tersimpan");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus item ini?")) return;
    try {
      await api.delete(`/admin/content/${collection}/${id}`);
      toast.success("Terhapus");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div data-testid={`panel-${collection}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-heading font-bold text-2xl">{title}</h2>
        <button onClick={() => setEditing(makeEmpty(items.length))} data-testid={`add-${collection}`} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition">
          <Plus className="w-4 h-4" /> Tambah
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-6 flex items-center gap-1.5"><GripVertical className="w-4 h-4" /> Seret kartu untuk mengubah urutan tampil di situs.</p>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
      ) : items.length === 0 ? (
        <p className="text-slate-500 text-sm">Belum ada data. Klik "Tambah".</p>
      ) : (
        <Reorder.Group axis="y" values={items} onReorder={setItems} className="space-y-3">
          {items.map((it) => {
            const sm = summary(it);
            return (
              <Reorder.Item
                key={it.id}
                value={it}
                onDragEnd={persistOrder}
                data-testid={`item-${collection}`}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-navy-card/50 p-3 pr-4 cursor-grab active:cursor-grabbing select-none"
              >
                <GripVertical className="w-5 h-5 text-slate-600 shrink-0" />
                {sm.image !== undefined && (
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-navy-soft shrink-0 grid place-items-center">
                    {sm.image ? <img src={resolveUrl(sm.image)} alt="" className="w-full h-full object-cover" draggable={false} /> : <span className="text-[9px] text-slate-600">n/a</span>}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold truncate">{sm.title}</p>
                  {sm.subtitle && <p className="text-xs text-slate-400 mt-0.5 truncate">{sm.subtitle}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onPointerDownCapture={stop} onClick={() => setEditing({ ...it })} data-testid={`edit-${collection}`} className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-slate-300 hover:text-cyan hover:border-cyan/40"><Pencil className="w-4 h-4" /></button>
                  <button onPointerDownCapture={stop} onClick={() => del(it.id)} data-testid={`delete-${collection}`} className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-slate-300 hover:text-red-400 hover:border-red-400/40"><Trash2 className="w-4 h-4" /></button>
                </div>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-navy/80 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-white/10 bg-navy-card p-6 md:p-8" data-testid={`form-${collection}`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-bold text-xl">{editing.id ? "Edit" : "Tambah"} {title}</h3>
                <button onClick={() => setEditing(null)} className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-slate-300 hover:bg-white/10"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-5">
                {fields.map((f) => (
                  <Field key={f.key} f={f} value={editing[f.key]} onChange={(v) => setEditing((s) => ({ ...s, [f.key]: v }))} />
                ))}
              </div>
              <div className="mt-8 flex gap-3 justify-end">
                <button onClick={() => setEditing(null)} className="rounded-lg border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/5">Batal</button>
                <button onClick={save} disabled={saving} data-testid={`save-${collection}`} className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-navy hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
